import { useContext, useState } from "react";
import { Col, Image, Row } from "react-bootstrap";
import ReactMarkdown from "react-markdown";
import PropTypes from "prop-types";
import "../../css/MegaMenu.css";
import { ActualLangContext } from "../../contexts/ActualLangContext";
import { buildNavbarUrl } from "../../utils/navigationHelpers";

const MegaMenuItem = ({ item, selected, setSelected, selectedLinks }) => {
  const { actualLang } = useContext(ActualLangContext);

  const isSelected = item.id === selected;
  return (
    <div
      onMouseEnter={() => setSelected(item.id)}
      className={isSelected ? "megaMenuItem selected" : "megaMenuItem"}
    >
      <a href={buildNavbarUrl(item.attributes.href, actualLang)}>
        <p>{item.attributes.title}</p>
        {item.attributes.slogan && (
          <p className="small-content">{item.attributes.slogan}</p>
        )}
      </a>
      {isSelected && (
        <div className="mobileLinks">
          {selectedLinks.map((link, index) => (
            <MegaMenuLink
              key={index}
              link={link}
              parentHref={item.attributes.href}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export const MegaMenu = ({ items }) => {
  const [selected, setSelected] = useState(items[0].id);
  const { actualLang } = useContext(ActualLangContext);

  // Obtener el item seleccionado actual
  const selectedItem = items.find((item) => item.id === selected);
  const selectedLinks = selectedItem?.attributes.links || [];

  return (
    <Row className="megaMenu">
      {/* Columna izquierda: Lista de items del menú */}
      <Col lg={{ offset: 1, span: 3 }} id="megaMenuItemContainer">
        {items.map((item, idx) => {
          return (
            <MegaMenuItem
              key={idx}
              item={item}
              selected={selected}
              setSelected={setSelected}
              selectedLinks={selectedLinks}
            />
          );
        })}
      </Col>

      {/* Columna derecha: Descripción del item seleccionado */}
      {selectedItem && (
        <Col
          lg={6}
          className="d-flex flex-column align-items-start"
          id="megaMenuItemDescription"
        >
          <Row>
            <Col lg={7}>
              <h4>
                <a href={buildNavbarUrl(selectedItem.attributes.href, actualLang)}>
                  {selectedItem.attributes.title}
                </a>
              </h4>
              <ReactMarkdown>{selectedItem.attributes.description}</ReactMarkdown>
            </Col>
            <Col lg={4}>
              {selectedItem?.attributes.image?.data?.attributes.url && <Image
                src={selectedItem.attributes.image?.data.attributes.url}
                alt={
                  selectedItem.attributes.image?.data.attributes?.alternativeText ||
                  "product image"
                }
                className="w-100"
              />}
            </Col>
          </Row>
          <SplitLinksColumns item={selectedItem} />
        </Col>
      )}
    </Row>
  );
};

/**
 * Componente para renderizar un link individual del mega menú
 */
const MegaMenuLink = ({ link, parentHref }) => {
  const { actualLang } = useContext(ActualLangContext);

  // Si es link externo, usar tal cual
  if (link.href.includes("https")) {
    return (
      <h5>
        <a href={link.href}>{link.title}</a>
      </h5>
    );
  }

  // Si no hay parentHref, no renderizar
  if (!parentHref) {
    return null;
  }

  // Construir URL combinando parent + link
  const langPrefix = actualLang === "en" ? "en-en" : "es-es";
  const fullUrl = `/${langPrefix}${parentHref}/${link.href}`;

  return (
    <h5>
      <a href={fullUrl}>{link.title}</a>
    </h5>
  );
};

/**
 * Componente que divide los links en columnas para mejor visualización
 * Si hay más de 4 links, los divide en 2 columnas
 */
const SplitLinksColumns = ({ item }) => {
  const links = item.attributes.links;
  const shouldSplit = links.length > 4;

  // Dividir en dos columnas si hay más de 4 links
  const half = Math.ceil(links.length / 2);
  const columns = shouldSplit ? [links.slice(0, half), links.slice(half)] : [links];

  return (
    <Row className={`linksContainer ${shouldSplit ? "columns" : ""}`}>
      {columns.map((column, colIndex) => (
        <Col key={colIndex} md={columns.length === 1 ? 12 : 6}>
          {column.map((link, index) => (
            <MegaMenuLink
              key={index}
              link={link}
              parentHref={item.attributes.href}
            />
          ))}
        </Col>
      ))}
    </Row>
  );
};

MegaMenuItem.propTypes = {
  item: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    attributes: PropTypes.shape({
      title: PropTypes.string.isRequired,
      slogan: PropTypes.string,
      href: PropTypes.string.isRequired,
      description: PropTypes.string,
      links: PropTypes.arrayOf(PropTypes.object),
      image: PropTypes.object,
    }).isRequired,
  }).isRequired,
  selected: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  setSelected: PropTypes.func.isRequired,
  selectedLinks: PropTypes.arrayOf(PropTypes.object).isRequired,
};

MegaMenu.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      attributes: PropTypes.shape({
        title: PropTypes.string.isRequired,
        slogan: PropTypes.string,
        href: PropTypes.string.isRequired,
        description: PropTypes.string,
        links: PropTypes.arrayOf(PropTypes.object),
        image: PropTypes.object,
      }).isRequired,
    })
  ).isRequired,
};

MegaMenuLink.propTypes = {
  link: PropTypes.shape({
    href: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
  }).isRequired,
  parentHref: PropTypes.string,
};

SplitLinksColumns.propTypes = {
  item: PropTypes.shape({
    attributes: PropTypes.shape({
      links: PropTypes.arrayOf(PropTypes.object).isRequired,
      href: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
};
